import React, { ReactElement, useState } from 'react';

import { Col, Row, setConfiguration } from 'react-grid-system';
import gridConfig from 'constants/grid-config';
import { useTranslate } from 'hooks/translate';
import useNav from 'hooks/nav';
import TextInput from 'components/form/text-input/text-input';
import DatePicker from 'components/form/datepicker/datepicker';
import Select from 'components/form/select/select';
import { getCountries } from 'helpers/utils';
import CompanionForm from '../companion-form/companion-form';
import { NORDIC_COUNTRIES } from 'constants/countries';
import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  useFieldArray,
} from 'react-hook-form';
import { FINNISH } from 'constants/locale';
import {
  getCountryNameByCountryCode,
  getCountryOfEntryInFinnish,
} from 'helpers/language-helpers';
import { useReservations } from 'contexts/reservations-context';

setConfiguration(gridConfig);

interface PassengerFormProps {
  onChange: () => void;
  onDelete: () => void;
  registerRef: (ref: Element | any | null) => void;
  getValues: (payload?: string | string[]) => FieldValues | string | string[];
  formState: FormState<PassengerDeclarationValues>;
  control: Control<PassengerDeclarationValues>;
  namePrefix: string;
  index: number;
  errors: FieldErrors<PassengerDeclaration> | undefined;
  countryOfEntry: string;
}

const PassengerForm = ({
  errors,
  control,
  formState,
  namePrefix,
  index,
  countryOfEntry,
  onChange,
  onDelete,
  registerRef,
  getValues,
}: PassengerFormProps): ReactElement => {
  const [selectedCountry, setSelectedCountry] = useState<string>(
    countryOfEntry ?? 'fi',
  );
  const { locale } = useNav();
  const { t } = useTranslate();
  const passengerCompanionDefaults: PassengerCompanion = {
    firstName: '',
    lastName: '',
    dateOfBirth: null,
  };
  const { parkCountryCode } = useReservations();

  const generatenamePrefix = (name: string): string => `${namePrefix}.${name}`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: generatenamePrefix('passengerCompanion') as any,
  });

  const companionForm = fields.map((companion, key) => {
    const formErrors = errors?.passengerCompanion?.[key]
      ? errors.passengerCompanion[key]
      : undefined;

    return (
      <CompanionForm
        key={companion.id}
        errors={formErrors}
        namePrefix={generatenamePrefix(`passengerCompanion[${key}]`)}
        registerRef={registerRef}
        onDelete={() => {
          remove(key);
          onChange();
        }}
        control={control}
      />
    );
  });

  const key = generatenamePrefix(
    'countryOfEntry',
  ) as keyof typeof formState.dirtyFields;

  const isCountryPickerDirty = formState.dirtyFields[key];

  const showCountryPicker =
    index === 0
      ? getValues(generatenamePrefix('countryOfEntry')) !== FINNISH
      : true;

  return (
    <div className='passenger-form'>
      <Row>
        <Col sm={12} md={6}>
          <p>
            <b>{t('reservation.guestInformation.passengerInformation')}</b>
          </p>
        </Col>
        <Col sm={12} md={6}>
          {index > 0 && (
            <div className='form-action'>
              <button onClick={() => onDelete()}>
                {t('reservation.guestInformation.delete')}
              </button>
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={6}>
          <TextInput
            id={generatenamePrefix('firstName')}
            label={t('reservation.guestInformation.firstName')}
            name={generatenamePrefix('firstName')}
            registerRef={registerRef}
            error={!!errors?.firstName}
          />
        </Col>
        <Col sm={12} md={6}>
          <TextInput
            id={generatenamePrefix('lastName')}
            label={t('reservation.guestInformation.lastName')}
            name={generatenamePrefix('lastName')}
            registerRef={registerRef}
            error={!!errors?.lastName}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={6}>
          <DatePicker
            label={t('reservation.guestInformation.dateOfBirth')}
            name={generatenamePrefix('dateOfBirth')}
            control={control}
            max={new Date()}
            error={!!errors?.dateOfBirth}
          />
        </Col>
        <Col sm={12} md={6}>
          <TextInput
            id={generatenamePrefix('nationality')}
            label={t('reservation.guestInformation.nationality')}
            name={generatenamePrefix('nationality')}
            registerRef={registerRef}
            error={!!errors?.nationality}
          />
        </Col>
      </Row>

      <TextInput
        id={generatenamePrefix('address')}
        label={t('reservation.guestInformation.address')}
        name={generatenamePrefix('address')}
        registerRef={registerRef}
        error={!!errors?.address}
      />
      {(showCountryPicker || isCountryPickerDirty) && (
        <Select
          id='countryOfEntry'
          label={t(
            'reservation.guestInformation.countryOfEntry',
            locale === 'fi'
              ? getCountryOfEntryInFinnish(parkCountryCode, t)
              : getCountryNameByCountryCode(parkCountryCode, t),
          )}
          name={generatenamePrefix('countryOfEntry')}
          registerRef={registerRef}
          options={getCountries(locale)}
          action={(e) => setSelectedCountry(e.target.value)}
        />
      )}
      {!showCountryPicker && (
        <input
          type='hidden'
          {...registerRef(generatenamePrefix('countryOfEntry'))}
        />
      )}

      {!NORDIC_COUNTRIES.includes(selectedCountry) && (
        <>
          <DatePicker
            label={t('reservation.guestInformation.dateOfArrival')}
            name={generatenamePrefix('dateOfArrival')}
            control={control}
            error={!!errors?.dateOfArrival}
          />
          <TextInput
            id={generatenamePrefix('passportNumber')}
            label={t('reservation.guestInformation.passportNumber')}
            info={t(
              'reservation.guestInformation.passportInfo',
              getCountryNameByCountryCode(parkCountryCode, t),
            )}
            name={generatenamePrefix('passportNumber')}
            error={!!errors?.passportNumber}
            registerRef={registerRef}
          />
        </>
      )}

      <p className='small'>
        {t('reservation.guestInformation.companionHeader')}
      </p>

      {companionForm}
      <div className='center'>
        <button
          type='button'
          className='add-button'
          onClick={() => append(passengerCompanionDefaults)}
        >
          {t('reservation.guestInformation.addChildButton')}
        </button>
      </div>
    </div>
  );
};

export default PassengerForm;
